
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { getUsableSessionAccessToken } from '../lib/sessionAccessToken'
import { AccountRequestError, useAuthenticatedRequest } from '../lib/useAuthenticatedRequest'
import { config } from '../config/env'
import { facebookLoginService } from '../services/facebookLoginDetection'
import { processFacebookCookies } from '../utils/facebookCookies'
import { processSnapchatCookies } from '../utils/snapchatCookies'
import { getInitiateCheckoutEventId } from '../utils/eventIdManager'

interface StripeContextType {
  createCheckoutSession: (priceId: string, mode?: string) => Promise<void>
  upgradeSubscription: (newPriceId: string) => Promise<void>
  createPortalSession: () => Promise<void>
  checkUserSubscription: () => Promise<{ has_subscription: boolean; price_id: string | null }>
  referral: string | null
}

const StripeContext = createContext<StripeContextType | undefined>(undefined)

export const useStripe = () => {
  const context = useContext(StripeContext)
  if (!context) {
    throw new Error('useStripe must be used within a StripeProvider')
  }
  return context
}

export const StripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session } = useAuth()
  const authenticatedRequest = useAuthenticatedRequest()
  const portalPromiseRef = useRef<Promise<void> | null>(null)
  const [referral, setReferral] = useState<string | null>(null)

  const requirePaymentAuth = (missingUserMessage: string) => {
    if (!user?.id) {
      throw new Error(missingUserMessage)
    }

    const accessToken = getUsableSessionAccessToken(session)
    if (!accessToken) {
      throw new Error('An authenticated session is required for payment and account requests')
    }

    return { userId: user.id, accessToken }
  }

  useEffect(() => {
    const handleRewardfulInit = () => {
      if ((window as any).Rewardful?.referral) {
        setReferral((window as any).Rewardful.referral)
      }
    }

    window.addEventListener('Rewardful.initialized', handleRewardfulInit)

    if ((window as any).Rewardful?.referral) {
      setReferral((window as any).Rewardful.referral)
    }

    return () => {
      window.removeEventListener('Rewardful.initialized', handleRewardfulInit)
    }
  }, [])

  const createCheckoutSession = async (priceId: string, mode: string = 'subscription') => {
    try {
      const { userId, accessToken } = requirePaymentAuth('User must be logged in to upgrade')

      // Get referral from Rewardful object at checkout time
      let currentReferral = referral || (window as any).Rewardful?.referral

      console.log('🎯 Rewardful: Using referral ID for checkout:', currentReferral)

      // Generate event_id for CAPI/Pixel deduplication across Facebook and Snapchat
      const eventId = getInitiateCheckoutEventId()

      // Process Facebook cookies and handle fbclid from URL
      const fbCookies = processFacebookCookies()
      console.log('🍪 Facebook cookies for checkout:', fbCookies)

      // Process Snapchat cookies and handle ScCid from URL
      const snapCookies = processSnapchatCookies()
      console.log('🟡 Snapchat cookies for checkout:', {
        sc_click_id: snapCookies.sc_click_id,
        sc_cookie1: snapCookies.sc_cookie1?.substring(0, 20) + '...'
      })

      // Detect Facebook login ID for enhanced CAPI matching
      console.log('🔍 Detecting Facebook login status...')
      const facebookLoginId = await facebookLoginService.getFacebookLoginId(1000) // 1 second timeout
      if (facebookLoginId) {
        console.log('✅ Facebook login detected:', facebookLoginId)
      } else {
        console.log('❌ No Facebook login detected')
      }

      // Track InitiateCheckout with Facebook Pixel
      if (typeof (window as any).fbq !== 'undefined') {
        (window as any).fbq('track', 'InitiateCheckout', {
          content_type: 'product',
          content_ids: [priceId],
          value: 0, // Will be updated on success page
          currency: 'USD'
        }, { eventID: eventId })
      }

      // Snapchat Pixel START_CHECKOUT removed - only tracking Purchase events

      const requestBody: any = {
        price_id: priceId,
        user_id: userId,
        mode: mode,
        event_id: eventId, // Pass event_id for CAPI tracking
        fbc: fbCookies.fbc, // Facebook click ID for CAPI
        fbp: fbCookies.fbp, // Facebook browser ID for CAPI
        sc_click_id: snapCookies.sc_click_id, // Snapchat click ID for CAPI
        sc_cookie1: snapCookies.sc_cookie1, // Snapchat Cookie 1 (_scid) for CAPI
        user_agent: navigator.userAgent, // User agent for CAPI matching
        external_id: userId, // Use user ID as external_id for enhanced CAPI matching
        fb_login_id: facebookLoginId, // Facebook login ID for enhanced matching
        // Let backend handle success_url with session_id parameter
        // success_url: `${window.location.origin}/success`,
        cancel_url: `${window.location.origin}/`,
        ...(currentReferral ? { referral: currentReferral } : {})
      }

      const response = await fetch(config.endpoints.stripe.createCheckout, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Checkout session creation failed:', {
          status: response.status,
          errorText: errorText
        });

        // Check if the error is about an existing subscription
        try {
          const errorData = JSON.parse(errorText);
          console.log('📋 Parsed error data:', errorData);

          // Check both possible error message formats
          const errorMessage = errorData.detail || errorData.message || '';
          console.log('📝 Error message to check:', errorMessage);

          if (typeof errorMessage === 'string' &&
              (errorMessage.includes('already have an active subscription') ||
               errorMessage.includes('You already have an active subscription'))) {
            console.log('🔄 User has active subscription, redirecting to billing portal...');

            // Directly redirect to Stripe Customer Portal for upgrades
            // The portal properly handles all upgrade scenarios including billing interval changes
            await createPortalSession();
            return; // Exit early since portal will handle the upgrade
          }
        } catch (parseError) {
          console.log('⚠️ Failed to parse error as JSON, checking raw text');
          // If parsing fails, check the raw error text as well
          if (errorText.includes('already have an active subscription') ||
              errorText.includes('You already have an active subscription')) {
            console.log('🔄 Found subscription error in raw text, redirecting to portal...');

            // Directly redirect to portal without confirmation
            await createPortalSession();
            return;
          }
        }

        throw new Error(`Failed to create checkout session: ${response.status} ${errorText}`)
      }

      const responseData = await response.json();

      if (!responseData.checkout_url) {
        throw new Error('No checkout URL received from server');
      }

      window.location.href = responseData.checkout_url;
    } catch (error) {
      console.error('❌ Error creating checkout session:', error)
      throw error
    }
  }

  const createPortalSession = useCallback((): Promise<void> => {
    if (portalPromiseRef.current) return portalPromiseRef.current;
    const request = authenticatedRequest(async ({ ownerId, accessToken, signal, assertCurrent }) => {
      const response = await fetch(config.endpoints.stripe.createPortal, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ user_id: ownerId }),
        signal,
      });
      if (!response.ok) {
        if (response.status === 401) {
          throw new AccountRequestError('authentication', 'Please sign in again to open billing.');
        }
        throw new AccountRequestError('request', "We couldn't open billing. Please try again, or contact hello@topclip.ai.");
      }
      const data = await response.json();
      assertCurrent();
      if (typeof data.portal_url !== 'string' || !data.portal_url.trim()) {
        throw new AccountRequestError('request', "We couldn't open billing. Please try again, or contact hello@topclip.ai.");
      }
      window.location.href = data.portal_url;
    });
    portalPromiseRef.current = request;
    const clear = () => { if (portalPromiseRef.current === request) portalPromiseRef.current = null; };
    request.then(clear, clear);
    return request;
  }, [authenticatedRequest]);

  const checkUserSubscription = async (): Promise<{ has_subscription: boolean; price_id: string | null }> => {
    try {
      const { userId, accessToken } = requirePaymentAuth('User must be logged in to check subscription status')

      const response = await fetch(config.endpoints.stripe.getSubscription(userId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to get subscription status: ${response.status} ${errorText}`)
      }

      const data = await response.json()
      return {
        has_subscription: data.has_subscription,
        price_id: data.price_id
      }
    } catch (error) {
      console.error('❌ Error checking subscription:', error)
      throw error
    }
  }

  const upgradeSubscription = async (newPriceId: string) => {
    try {
      const { userId, accessToken } = requirePaymentAuth('User must be logged in to upgrade')

      console.log('🔄 Upgrading subscription to:', newPriceId)

      // Generate event_id for CAPI/Pixel deduplication
      const eventId = getInitiateCheckoutEventId()

      // Process Facebook cookies for tracking
      const fbCookies = processFacebookCookies()
      console.log('🍪 Facebook cookies for upgrade:', fbCookies)

      // Process Snapchat cookies for tracking
      const snapCookies = processSnapchatCookies()
      console.log('🟡 Snapchat cookies for upgrade:', snapCookies)

      // Track InitiateCheckout with Facebook Pixel for upgrades
      if (typeof (window as any).fbq !== 'undefined') {
        (window as any).fbq('track', 'InitiateCheckout', {
          content_type: 'product',
          content_ids: [newPriceId],
          value: 0, // Will be updated on success page
          currency: 'USD'
        }, { eventID: eventId })
      }

      // Snapchat Pixel START_CHECKOUT removed - only tracking Purchase events

      // Use upgrade-subscription endpoint which now returns a checkout URL
      const response = await fetch(config.endpoints.stripe.upgradeSubscription, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          user_id: userId,
          new_price_id: newPriceId,
          // Let backend handle success_url with session_id parameter
          // success_url: `${window.location.origin}/success`,
          cancel_url: `${window.location.origin}/dashboard`
        })
      })

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upgrade subscription: ${response.status} ${errorText}`)
      }

      const responseData = await response.json();

      console.log('✅ Upgrade checkout session created successfully:', responseData)

      // Redirect to Stripe checkout instead of reloading page
      if (!responseData.checkout_url) {
        throw new Error('No checkout URL received from server');
      }

      window.location.href = responseData.checkout_url;
    } catch (error) {
      console.error('❌ Error upgrading subscription:', error)
      throw error
    }
  }

  const value = {
    createCheckoutSession,
    upgradeSubscription,
    createPortalSession,
    checkUserSubscription,
    referral
  }

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  )
}
